"use client";

import { type FC } from "react";
import { Loader, PanelRightClose, PanelRightOpen } from "lucide-react";
import { ErrorBoundary } from "@/elements/components/assistant-ui/error-boundary";
import { Thread } from "@/elements/components/assistant-ui/thread";
import { ThreadList } from "@/elements/components/assistant-ui/thread-list";
import { TooltipIconButton } from "@/elements/components/assistant-ui/tooltip-icon-button";
import { useThemeProps } from "@/elements/hooks/useThemeProps";
import { useElements } from "@/elements/hooks/useElements";
import { cn } from "@/lib/utils";
import { useExpanded } from "@/elements/hooks/useExpanded";
import { LazyMotion, domMax } from "motion/react";
import * as m from "motion/react-m";
import { EASE_OUT_QUINT } from "@/elements/lib/easing";
import { useAuiState } from "@assistant-ui/react";

interface AssistantSidecarProps {
  className?: string;
}

export const AssistantSidecar: FC<AssistantSidecarProps> = ({ className }) => {
  const { config } = useElements();
  const themeProps = useThemeProps();
  const sidecarConfig = config.sidecar ?? {};
  const { title, dimensions } = sidecarConfig;
  const { expandable, isExpanded, setIsExpanded } = useExpanded();
  const isGenerating = useAuiState(({ thread }) =>
    thread.messages.some((message) => message.status?.type === "running"),
  );

  // Check if thread list should be shown
  const showThreadList =
    config.history?.enabled && config.history?.showThreadList !== false;

  return (
    <LazyMotion features={domMax}>
      <m.div
        initial={false}
        animate={{
          width: isExpanded
            ? (dimensions?.expanded?.width ?? "800px")
            : (dimensions?.default?.width ?? "400px"),
        }}
        transition={{ duration: 0.3, ease: EASE_OUT_QUINT }}
        className={cn(
          "aui-root aui-sidecar fixed top-0 right-0 bottom-0 flex flex-col border-l bg-popover text-popover-foreground",
          themeProps.className,
          className,
        )}
      >
        {/* Header */}
        <div className="aui-sidecar-header flex h-14 shrink-0 items-center justify-between border-b px-4">
          <span
            className={cn(
              "text-md flex items-center gap-2 font-medium text-foreground",
              isGenerating && "title-shimmer",
            )}
          >
            {title}

            {isGenerating && (
              <Loader
                className="size-4.5 animate-spin text-muted-foreground"
                strokeWidth={1.25}
              />
            )}
          </span>
          {expandable && (
            <div className="aui-sidecar-header-actions flex items-center gap-1">
              <TooltipIconButton
                tooltip={isExpanded ? "Collapse" : "Pop out"}
                variant="ghost"
                className="aui-sidecar-popout size-8"
                onClick={() => setIsExpanded((v) => !v)}
              >
                {!isExpanded ? (
                  <PanelRightOpen className="size-4.5" />
                ) : (
                  <PanelRightClose className="size-4.5" />
                )}
              </TooltipIconButton>
            </div>
          )}
        </div>

        {/* Main content area */}
        <div className="aui-sidecar-body flex min-h-0 flex-1 overflow-hidden">
          {/* Thread list sidebar (when history enabled) */}
          {showThreadList && (
            <div className="aui-sidecar-thread-list w-56 shrink-0 overflow-y-auto border-r">
              <ThreadList />
            </div>
          )}

          {/* Thread content */}
          <div className="aui-sidecar-content flex-1 overflow-hidden">
            <ErrorBoundary>
              <Thread />
            </ErrorBoundary>
          </div>
        </div>
      </m.div>
    </LazyMotion>
  );
};
